import React from 'react';
import { ReactAmChart } from '../../../ReactAmChart';
import ReactDOMServer from 'react-dom/server';
import { arrayOf, shape, string, number, bool } from 'prop-types';
import { isEqual, get } from 'lodash';
import styles from './styles.scss';

export default class PagePathChart extends ReactAmChart {
  static propTypes = {
    data: arrayOf(shape({
      y: string.isRequired,
      x: number.isRequired,
    })).isRequired,
    fontColor: string,
    xAxisTitle: string,
    yAxisTitle: string,
    fontFamily: string,
    defaultFillColor: string,
    axisFontSize: number,
    cellSize: number
  }

  static defaultProps = {
    cellSize: 40,
    axisFontSize: 12,
    defaultFillColor: '#67B7DC',
    xAxisColor: 'rgba(0, 0, 0, 0.38)',
    yAxisColor: 'rgba(0, 0, 0, 0.87)',
    fontFamily: 'Roboto, "Helvetica Neue", sans-serif'
  }

  categoryAxis;
  valueAxis;
  columnSeries;
  prevChartData;
  drawFunction(chartId, prevChart, am4core, am4charts) {
    const { 
      data,
      xAxisColor,
      yAxisColor,
      defaultFillColor,
      fontFamily,
      axisFontSize,
      cellSize,
    } = this.props;
    let chart = prevChart;
    
    if (!chart) {
      chart = am4core.create(chartId, am4charts.XYChart);
      const am4yAxisColor = am4core.color(yAxisColor);
      
      // set category Y
      this.categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
      this.categoryAxis.dataFields.category = 'y';
      this.categoryAxis.renderer.inversed = true;
      this.categoryAxis.renderer.minGridDistance = 30;
      this.categoryAxis.renderer.grid.template.location = 0;
      this.categoryAxis.renderer.labels.template.align = 'left';
      this.categoryAxis.renderer.labels.template.paddingRight = '20';
      this.categoryAxis.renderer.labels.template.truncate = true //truncate the letters
      this.categoryAxis.renderer.labels.template.hideOversized = true;
      this.categoryAxis.renderer.labels.template.maxWidth = 500;
      this.categoryAxis.renderer.labels.template.fill = am4yAxisColor;
      this.categoryAxis.cursorTooltipEnabled = false;
      this.categoryAxis.fontSize = axisFontSize;
      this.categoryAxis.fontFamily = fontFamily;
      
      // only show top & bottom grid
      this.categoryAxis.renderer.grid.template.adapter.add('visible', (value, target) => {
        return target.dataItem.index === -1 || target.dataItem.index === 0;
      })

      // set x axis
      this.valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
      this.valueAxis.min = 0;
      this.valueAxis.renderer.minGridDistance = 100;
      this.valueAxis.cursorTooltipEnabled = false;  
      this.valueAxis.renderer.labels.template.fill = xAxisColor;
      this.valueAxis.fontSize = axisFontSize;
      this.valueAxis.fontFamily = fontFamily;

      //set column
      this.columnSeries = chart.series.push(new am4charts.ColumnSeries());
      this.columnSeries.yAxes = this.categoryAxis;
      this.columnSeries.strokeWidth = 0;
      this.columnSeries.dataFields.valueX = 'x';
      this.columnSeries.dataFields.categoryY = 'y';
      this.columnSeries.cursorTooltipEnabled = false; 
      this.columnSeries.sequencedInterpolation = true;
      this.columnSeries.columns.template.height = 20;
      this.columnSeries.columns.template.fill = am4core.color(defaultFillColor);
      this.columnSeries.columns.template.propertyFields.fill = 'columnColor';
      this.columnSeries.tooltip.getFillFromObject = false;
      this.columnSeries.tooltip.background.fill = '#ffffff';

      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineX.strokeWidth = 0;
      chart.cursor.lineY.strokeWidth = 0;
      chart.cursor.behavior = 'none';
    }

    if (!isEqual(this.prevChartData, data)) {
      chart.data = data;
      this.prevChartData = data;

      // adjust chart height
      // https://www.amcharts.com/docs/v4/tutorials/auto-adjusting-chart-height-based-on-a-number-of-data-items/
      chart.events.on('datavalidated', function(e) {
        const chart = e.target;
        const categoryAxis = chart.yAxes.getIndex(0);
        const adjustHeight = get(chart, 'data', []).length * cellSize - categoryAxis.pixelHeight;
        const targetHeight = chart.pixelHeight + adjustHeight;
        const height = Math.max(targetHeight, cellSize * 10);
        chart.svgContainer.htmlElement.style.height = `${height}px`;
      });

      this.columnSeries.columns.template.tooltipHTML = ReactDOMServer.renderToString(this.drawToolTip());
    }

    return chart;
  }

  drawToolTip() {
    const { xAxisTitle } = this.props;
    return ( 
      <div className={styles.pagePathChartToolTip}>
        <div className={styles.title}>{'{categoryY}'}</div>
        <div className={styles.divider}/>
        <div className={styles.data}>
          <span className={styles.dataTitle}>{xAxisTitle}</span>
          <span className={styles.value}>{`{valueX}`}</span>
        </div>
      </div>
    );
  }
}
